import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {Building2, Save, Loader2, CheckCircle2, AlertCircle, Upload, X, Move, ZoomIn} from 'lucide-react'
import EmpresaLayout from '@/components/EmpresaLayout'
import { lumi } from '@/lib/lumi'
import { useAuth } from '@/hooks/useAuth'
import { INDUSTRIES } from '@/lib/industries'
import { checkCompanyDuplicate } from '@/lib/duplicateDetection'

// Giros unificados con la búsqueda de vacantes (src/lib/industries.ts)
const INDUSTRY_OPTIONS = INDUSTRIES

const EMPLOYEE_OPTIONS = ['1-10', '11-50', '51-200', '201-500', '501-1000', 'Más de 1000']

// Redes sociales para empresa
const SOCIAL_NETWORKS = [
  { key: 'linkedin', label: 'LinkedIn', placeholder: 'https://linkedin.com/company/tuempresa', color: 'bg-blue-700', icon: 'in' },
  { key: 'facebook', label: 'Facebook', placeholder: 'https://facebook.com/tuempresa', color: 'bg-blue-600', icon: 'f' },
  { key: 'instagram', label: 'Instagram', placeholder: 'https://instagram.com/tuempresa', color: 'bg-pink-600', icon: 'ig' },
  { key: 'twitter', label: 'X / Twitter', placeholder: 'https://x.com/tuempresa', color: 'bg-slate-900', icon: 'x' },
  { key: 'youtube', label: 'YouTube', placeholder: 'https://youtube.com/@tuempresa', color: 'bg-red-600', icon: 'yt' },
  { key: 'tiktok', label: 'TikTok', placeholder: 'https://tiktok.com/@tuempresa', color: 'bg-slate-800', icon: 'tt' },
  { key: 'whatsapp', label: 'WhatsApp', placeholder: 'https://wa.me/521234567890', color: 'bg-green-600', icon: 'wa' },
]

type SocialLinks = Record<string, string>

function EmpresaPerfil() {
  const { user, isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const logoInputRef = useRef<HTMLInputElement>(null)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState('')
  const [profileId, setProfileId] = useState<string | null>(null)
  const [uploadingLogo, setUploadingLogo] = useState(false)
  const [logoOffset, setLogoOffset] = useState({ x: 50, y: 50 })
  const [isDraggingLogo, setIsDraggingLogo] = useState(false)
  const logoDragRef = useRef<{ startX: number; startY: number; ox: number; oy: number } | null>(null)
  const logoContainerRef = useRef<HTMLDivElement>(null)
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [activeSocialNet, setActiveSocialNet] = useState<string | null>(null)
  const [socialLinks, setSocialLinks] = useState<SocialLinks>({})
  const [form, setForm] = useState({
    tradeName: '', legalName: '', rfc: '', industry: '', industryOther: '',
    address: '', city: '', state: '', country: 'México',
    phone: '', whatsapp: '', email: '', website: '',
    contactName: '', contactRole: '',
    description: '', employeeCount: '',
    logoUrl: '',
  })

  useEffect(() => {
    if (!isAuthenticated) { navigate('/login'); return }
    const load = async () => {
      if (!user) return
      try {
        const res = await lumi.entities.companies.list({ filter: { userId: user.userId }, limit: 1 })
        if (res.list.length > 0) {
          const c = res.list[0]
          setProfileId(c._id)
          // Determinar si el giro guardado está en la lista o es personalizado
          const industryVal = c.industry || ''
          const isCustom = industryVal && !INDUSTRY_OPTIONS.slice(0, -1).includes(industryVal)
          setForm({
            tradeName: c.tradeName || '', legalName: c.legalName || '', rfc: c.rfc || '',
            industry: isCustom ? 'Otro' : industryVal,
            industryOther: isCustom ? industryVal : '',
            address: c.address || '', city: c.city || '',
            state: c.state || '', country: c.country || 'México', phone: c.phone || '',
            whatsapp: c.whatsapp || '',
            email: c.email || user.email, website: c.website || '',
            contactName: c.contactName || '', contactRole: c.contactRole || '',
            description: c.description || '',
            employeeCount: c.employeeCount ? String(c.employeeCount) : '',
            logoUrl: c.logoUrl || '',
          })
          if (c.logoOffset && typeof c.logoOffset === 'object') {
            setLogoOffset(c.logoOffset as { x: number; y: number })
          }
          // Cargar redes sociales guardadas
          if (c.socialLinks && typeof c.socialLinks === 'object') {
            setSocialLinks(c.socialLinks as SocialLinks)
          } else if (typeof c.socialLinks === 'string' && c.socialLinks) {
            // compatibilidad con formato anterior (string)
            setSocialLinks({ linkedin: c.socialLinks })
          }
        } else {
          setForm((f) => ({ ...f, email: user.email }))
        }
      } catch (e) { console.error(e) }
    }
    load()
  }, [isAuthenticated, user])

  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }))

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploadingLogo(true)
    try {
      const results = await lumi.tools.file.upload([file])
      const result = results[0]
      if (result.fileUrl) {
        set('logoUrl', result.fileUrl)
      } else {
        setError(result.uploadError || 'Error al subir el logo.')
      }
    } catch (err: any) {
      setError(err?.message || 'Error al subir el logo.')
    } finally {
      setUploadingLogo(false)
      if (logoInputRef.current) logoInputRef.current.value = ''
    }
  }

  const setSocialLink = (key: string, value: string) => {
    setSocialLinks((prev) => ({ ...prev, [key]: value }))
  }

  const removeSocialLink = (key: string) => {
    setSocialLinks((prev) => { const next = { ...prev }; delete next[key]; return next })
    if (activeSocialNet === key) setActiveSocialNet(null)
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!user) return
    setError(''); setSaving(true); setSaved(false)
    try {
      const finalIndustry = form.industry === 'Otro' ? (form.industryOther.trim() || 'Otro') : form.industry
      const payload = {
        userId: user.userId, tradeName: form.tradeName, legalName: form.legalName,
        rfc: form.rfc, industry: finalIndustry, address: form.address, city: form.city,
        state: form.state, country: form.country, phone: form.phone,
        whatsapp: form.whatsapp || undefined, email: form.email,
        website: form.website, contactName: form.contactName, contactRole: form.contactRole,
        socialLinks: socialLinks, description: form.description,
        employeeCount: form.employeeCount || undefined,
        logoUrl: form.logoUrl, logoOffset, updatedAt: new Date().toISOString(),
      }
      if (profileId) {
        await lumi.entities.companies.update(profileId, payload)
      } else {
        const created = await lumi.entities.companies.create({ ...payload, createdAt: new Date().toISOString() })
        setProfileId(created._id)
        const profRes = await lumi.entities.profiles.list({ filter: { userId: user.userId }, limit: 1 })
        if (profRes.list.length > 0) {
          await lumi.entities.profiles.update(profRes.list[0]._id, { onboardingCompleted: true, updatedAt: new Date().toISOString() })
        }
        // Detecta posibles empresas duplicadas y notifica al admin para revisión/anulación
        await checkCompanyDuplicate({
          userId: user.userId,
          tradeName: form.tradeName,
          rfc: form.rfc,
          email: form.email,
        })
      }
      setSaved(true)
      setTimeout(() => {
        setSaved(false)
        navigate('/empresa/dashboard')
      }, 1200)
    } catch (e: any) {
      setError(e?.message || 'Error al guardar. Intenta de nuevo.')
    } finally {
      setSaving(false)
    }
  }

  const inputCls = 'w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm text-slate-900 placeholder-slate-400 outline-none transition focus:border-amber-400 focus:ring-2 focus:ring-amber-100'
  const labelCls = 'mb-1.5 block text-sm font-medium text-slate-700'

  const activeSocialConfig = activeSocialNet ? SOCIAL_NETWORKS.find((n) => n.key === activeSocialNet) : null

  // Drag handlers para reposicionar el logo
  const handleLogoDragStart = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (!form.logoUrl) return
    e.preventDefault()
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY
    logoDragRef.current = { startX: clientX, startY: clientY, ox: logoOffset.x, oy: logoOffset.y }
    setIsDraggingLogo(true)
  }, [form.logoUrl, logoOffset])

  const handleLogoDragMove = useCallback((e: MouseEvent | TouchEvent) => {
    if (!logoDragRef.current || !logoContainerRef.current) return
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY
    const rect = logoContainerRef.current.getBoundingClientRect()
    const dx = clientX - logoDragRef.current.startX
    const dy = clientY - logoDragRef.current.startY
    const newX = Math.max(0, Math.min(100, logoDragRef.current.ox + (dx / rect.width) * 100))
    const newY = Math.max(0, Math.min(100, logoDragRef.current.oy + (dy / rect.height) * 100))
    setLogoOffset({ x: newX, y: newY })
  }, [])

  const handleLogoDragEnd = useCallback(() => {
    logoDragRef.current = null
    setIsDraggingLogo(false)
  }, [])

  useEffect(() => {
    if (isDraggingLogo) {
      window.addEventListener('mousemove', handleLogoDragMove)
      window.addEventListener('mouseup', handleLogoDragEnd)
      window.addEventListener('touchmove', handleLogoDragMove, { passive: false })
      window.addEventListener('touchend', handleLogoDragEnd)
    }
    return () => {
      window.removeEventListener('mousemove', handleLogoDragMove)
      window.removeEventListener('mouseup', handleLogoDragEnd)
      window.removeEventListener('touchmove', handleLogoDragMove)
      window.removeEventListener('touchend', handleLogoDragEnd)
    }
  }, [isDraggingLogo, handleLogoDragMove, handleLogoDragEnd])

  return (
    <EmpresaLayout
      title="Perfil de empresa"
      subtitle="Completa la información de tu organización para atraer al mejor talento"
    >
      <div className="mx-auto max-w-3xl">
        <form onSubmit={handleSave} className="space-y-6">

          {/* Logo */}
          <div className="rounded-xl border border-slate-200 bg-white p-6">
            <h2 className="mb-5 text-sm font-semibold uppercase tracking-wider text-slate-400">Logotipo</h2>
            <div className="flex items-start gap-5">
              {/* Vista previa con drag y clic para ampliar */}
              <div
                ref={logoContainerRef}
                className={`relative flex h-24 w-24 shrink-0 items-center justify-center rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 overflow-hidden select-none ${form.logoUrl ? (isDraggingLogo ? 'cursor-grabbing' : 'cursor-grab') : ''}`}
                onMouseDown={handleLogoDragStart}
                onTouchStart={handleLogoDragStart}
              >
                {form.logoUrl ? (
                  <>
                    <img
                      src={form.logoUrl}
                      alt="Logo"
                      draggable={false}
                      className="absolute h-auto w-auto max-h-none max-w-none"
                      style={{
                        minWidth: '100%',
                        minHeight: '100%',
                        objectFit: 'cover',
                        objectPosition: `${logoOffset.x}% ${logoOffset.y}%`,
                        width: '100%',
                        height: '100%',
                      }}
                    />
                    <button
                      type="button"
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={() => { set('logoUrl', ''); setLogoOffset({ x: 50, y: 50 }) }}
                      className="absolute top-1 right-1 z-10 rounded-full bg-white/90 p-0.5 text-slate-500 shadow hover:text-red-600"
                    >
                      <X size={13} />
                    </button>
                    <button
                      type="button"
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => { e.stopPropagation(); setLightboxOpen(true) }}
                      className="absolute bottom-1 right-1 z-10 rounded-md bg-black/50 p-0.5 text-white hover:bg-black/70"
                      title="Ver imagen"
                    >
                      <ZoomIn size={11} />
                    </button>
                    <div className="absolute bottom-1 left-1 z-10 rounded-md bg-black/40 p-0.5">
                      <Move size={11} className="text-white" />
                    </div>
                  </>
                ) : (
                  <Building2 size={32} className="text-slate-300" />
                )}
              </div>
              <div className="flex-1">
                <p className="mb-3 text-sm text-slate-600">Sube el logotipo de tu empresa. Formatos: PNG, JPG, WEBP. Máx. 10 MB.</p>
                {form.logoUrl && (
                  <p className="mb-2 text-xs text-slate-400 flex items-center gap-1">
                    <Move size={11} /> Arrastra el logo para centrar o reposicionar
                  </p>
                )}
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif"
                  className="hidden"
                  onChange={handleLogoUpload}
                />
                <button
                  type="button"
                  disabled={uploadingLogo}
                  onClick={() => logoInputRef.current?.click()}
                  className="flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-60"
                >
                  {uploadingLogo ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
                  {uploadingLogo ? 'Subiendo...' : form.logoUrl ? 'Cambiar imagen' : 'Seleccionar imagen'}
                </button>
                {form.logoUrl && (
                  <p className="mt-2 text-xs text-emerald-600 flex items-center gap-1">
                    <CheckCircle2 size={12} /> Logo cargado correctamente
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Identificación */}
          <div className="rounded-xl border border-slate-200 bg-white p-6">
            <h2 className="mb-5 text-sm font-semibold uppercase tracking-wider text-slate-400">Identificación</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className={labelCls}>Nombre comercial *</label>
                <input className={inputCls} value={form.tradeName} onChange={(e) => set('tradeName', e.target.value)} placeholder="Nombre de tu empresa" required />
              </div>
              <div>
                <label className={labelCls}>Razón social</label>
                <input className={inputCls} value={form.legalName} onChange={(e) => set('legalName', e.target.value)} placeholder="Razón social oficial" />
              </div>
              <div>
                <label className={labelCls}>RFC</label>
                <input className={inputCls} value={form.rfc} onChange={(e) => set('rfc', e.target.value)} placeholder="RFC de la empresa" />
              </div>
              <div>
                <label className={labelCls}>Giro empresarial</label>
                <select className={inputCls} value={form.industry} onChange={(e) => set('industry', e.target.value)}>
                  <option value="">Seleccionar giro</option>
                  {INDUSTRY_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              {form.industry === 'Otro' && (
                <div>
                  <label className={labelCls}>Especifica el giro *</label>
                  <input
                    className={inputCls}
                    value={form.industryOther}
                    onChange={(e) => set('industryOther', e.target.value)}
                    placeholder="Describe el giro de tu empresa"
                    required
                  />
                </div>
              )}
              <div>
                <label className={labelCls}>Número de empleados</label>
                <select className={inputCls} value={form.employeeCount} onChange={(e) => set('employeeCount', e.target.value)}>
                  <option value="">Seleccionar rango</option>
                  {EMPLOYEE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Contacto */}
          <div className="rounded-xl border border-slate-200 bg-white p-6">
            <h2 className="mb-5 text-sm font-semibold uppercase tracking-wider text-slate-400">Contacto y ubicación</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className={labelCls}>Dirección</label>
                <input className={inputCls} value={form.address} onChange={(e) => set('address', e.target.value)} placeholder="Calle, número, colonia" />
              </div>
              <div>
                <label className={labelCls}>Ciudad</label>
                <input className={inputCls} value={form.city} onChange={(e) => set('city', e.target.value)} placeholder="Ciudad" />
              </div>
              <div>
                <label className={labelCls}>Estado</label>
                <input className={inputCls} value={form.state} onChange={(e) => set('state', e.target.value)} placeholder="Estado" />
              </div>
              <div>
                <label className={labelCls}>País</label>
                <input className={inputCls} value={form.country} onChange={(e) => set('country', e.target.value)} placeholder="País" />
              </div>
              <div>
                <label className={labelCls}>Teléfono</label>
                <input className={inputCls} value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="+52 000 000 0000" />
              </div>
              <div>
                <label className={labelCls}>WhatsApp</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-bold text-green-600 select-none pointer-events-none">WA</span>
                  <input
                    className="w-full rounded-lg border border-slate-300 pl-10 pr-3.5 py-2.5 text-sm text-slate-900 placeholder-slate-400 outline-none transition focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
                    value={form.whatsapp}
                    onChange={(e) => set('whatsapp', e.target.value)}
                    placeholder="+52 55 1234 5678"
                    inputMode="tel"
                  />
                </div>
                <p className="mt-1 text-xs text-slate-400">Número celular de contacto directo por WhatsApp</p>
              </div>
              <div>
                <label className={labelCls}>Correo</label>
                <input type="email" className={inputCls} value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="contacto@empresa.com" />
              </div>
              <div>
                <label className={labelCls}>Sitio web</label>
                <input className={inputCls} value={form.website} onChange={(e) => set('website', e.target.value)} placeholder="https://miempresa.com" />
              </div>
            </div>
          </div>

          {/* Responsable */}
          <div className="rounded-xl border border-slate-200 bg-white p-6">
            <h2 className="mb-5 text-sm font-semibold uppercase tracking-wider text-slate-400">Responsable</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className={labelCls}>Nombre del responsable</label>
                <input className={inputCls} value={form.contactName} onChange={(e) => set('contactName', e.target.value)} placeholder="Nombre completo" />
              </div>
              <div>
                <label className={labelCls}>Cargo</label>
                <input className={inputCls} value={form.contactRole} onChange={(e) => set('contactRole', e.target.value)} placeholder="Director, Gerente HR, etc." />
              </div>
            </div>
          </div>

          {/* Redes sociales */}
          <div className="rounded-xl border border-slate-200 bg-white p-6">
            <h2 className="mb-1 text-sm font-semibold uppercase tracking-wider text-slate-400">Redes sociales</h2>
            <p className="mb-4 text-xs text-slate-500">Selecciona una red social y pega el enlace de tu perfil.</p>

            {/* Botones de redes */}
            <div className="mb-4 flex flex-wrap gap-2">
              {SOCIAL_NETWORKS.map((net) => {
                const hasLink = !!socialLinks[net.key]
                return (
                  <button
                    key={net.key}
                    type="button"
                    onClick={() => setActiveSocialNet(activeSocialNet === net.key ? null : net.key)}
                    className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold text-white transition
                      ${net.color} ${activeSocialNet === net.key ? 'ring-2 ring-offset-1 ring-amber-400' : ''}
                      ${hasLink ? 'opacity-100' : 'opacity-60 hover:opacity-90'}`}
                  >
                    <span className="uppercase font-bold">{net.icon}</span>
                    {net.label}
                    {hasLink && <CheckCircle2 size={12} className="ml-0.5" />}
                  </button>
                )
              })}
            </div>

            {/* Campo activo */}
            {activeSocialConfig && (
              <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
                <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-xs font-bold text-white ${activeSocialConfig.color}`}>
                  {activeSocialConfig.icon.toUpperCase()}
                </span>
                <input
                  className="flex-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder-slate-400 outline-none transition focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
                  value={socialLinks[activeSocialConfig.key] || ''}
                  onChange={(e) => setSocialLink(activeSocialConfig.key, e.target.value)}
                  placeholder={activeSocialConfig.placeholder}
                />
                {socialLinks[activeSocialConfig.key] && (
                  <button type="button" onClick={() => removeSocialLink(activeSocialConfig.key)} className="rounded-lg p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50">
                    <X size={15} />
                  </button>
                )}
              </div>
            )}

            {/* Resumen de links activos */}
            {Object.keys(socialLinks).filter((k) => socialLinks[k]).length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {Object.entries(socialLinks).filter(([, v]) => v).map(([key, url]) => {
                  const net = SOCIAL_NETWORKS.find((n) => n.key === key)
                  if (!net) return null
                  return (
                    <a
                      key={key}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-700 hover:bg-slate-200 transition"
                    >
                      <span className={`inline-flex h-4 w-4 items-center justify-center rounded-full text-white text-xs font-bold ${net.color}`} style={{ fontSize: 8 }}>
                        {net.icon.toUpperCase()}
                      </span>
                      {net.label}
                    </a>
                  )
                })}
              </div>
            )}
          </div>

          {/* Descripción */}
          <div className="rounded-xl border border-slate-200 bg-white p-6">
            <h2 className="mb-5 text-sm font-semibold uppercase tracking-wider text-slate-400">Descripción</h2>
            <textarea
              rows={4}
              className={inputCls}
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              placeholder="Describe tu empresa, cultura, valores y misión..."
            />
          </div>

          {error && (
            <div className="flex items-start gap-2 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
              <AlertCircle size={15} className="mt-0.5 shrink-0" /><span>{error}</span>
            </div>
          )}

          <div className="flex items-center justify-between">
            {saved && (
              <span className="flex items-center gap-1.5 text-sm text-emerald-600">
                <CheckCircle2 size={16} /> Cambios guardados correctamente
              </span>
            )}
            <div className="ml-auto">
              <button type="submit" disabled={saving} className="flex items-center gap-2 rounded-lg bg-slate-900 px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:opacity-60">
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                {saving ? 'Guardando...' : 'Guardar perfil'}
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Lightbox logo */}
      {lightboxOpen && form.logoUrl && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4"
          onClick={() => setLightboxOpen(false)}
        >
          <div className="relative max-w-3xl w-full" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setLightboxOpen(false)}
              className="absolute -top-10 right-0 flex items-center gap-1.5 rounded-lg bg-white/10 px-3 py-1.5 text-sm text-white hover:bg-white/20"
            >
              <X size={15} /> Cerrar
            </button>
            <img
              src={form.logoUrl}
              alt="Logo de empresa"
              className="mx-auto max-h-[80vh] w-auto rounded-xl shadow-2xl"
            />
          </div>
        </div>
      )}
    </EmpresaLayout>
  )
}

export default EmpresaPerfil
